// Home v2 — public/private mode toggle pill. Extracted from BalanceCard so any
// screen (balance card, Activity toolbar, …) can switch wallet mode with the
// same control. Active segment paints the mode gradient (blue=public,
// orange=private); private is gated by `privateModeReason` and offers retry.
import React from 'react'
import { Pressable, View } from 'react-native'

import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ShieldFilledIcon from '@common/assets/svg/ShieldFilledIcon'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import { i18nKey } from '@common/config/localization/constants'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import { MODE_TOGGLE_GRADIENT_CSS, RADII } from '../tokens'

type Props = {
  mode: Mode
  onModeChange: (mode: Mode) => void
  privateModeReason: PrivateModeReason
  onRetryRailgunInit: () => void
  /** Fill the container width and split segments evenly. */
  stretch?: boolean
  /** Adapt container/inactive colors for a light surface (default = dark card). */
  light?: boolean
  /** Shrink the pill (smaller padding/icon/text) for tight mobile rows. */
  compact?: boolean
}

const ModeToggle: React.FC<Props> = ({
  mode,
  onModeChange,
  privateModeReason,
  onRetryRailgunInit,
  stretch,
  light,
  compact
}) => {
  const { t } = useTranslation()
  const inactiveText = light ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.72)'
  const iconSize = compact ? 12 : 16
  const fontSize = compact ? 11 : 14
  const segGap = compact ? 3 : 8
  const opts: { id: Mode; label: string; Icon: typeof GlobeIcon }[] = [
    { id: ModeEnum.PUBLIC, label: i18nKey('Public'), Icon: GlobeIcon },
    { id: ModeEnum.PRIVATE, label: i18nKey('Private'), Icon: ShieldFilledIcon }
  ]
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: 2,
        borderRadius: RADII.pill,
        backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.45)',
        borderWidth: 1,
        borderColor: light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.12)',
        alignSelf: stretch ? 'stretch' : 'flex-start',
        flex: stretch ? 1 : undefined
      }}
    >
      {opts.map(({ id, label, Icon }) => {
        const isOn = mode === id
        const isPrivate = id === ModeEnum.PRIVATE
        const unavailable = isPrivate && privateModeReason !== 'available'
        const allowRetry = isPrivate && privateModeReason === 'failed'
        // 'creatable' = seed account with no Railgun wallet yet. Keep it CLICKABLE:
        // clicking opts into private mode, which lazily boots the engine and creates
        // the wallet (init-on-demand). Only permanently-blocked reasons (no-seed /
        // hardware / view-only) and the transient 'initializing' spinner state are
        // truly non-pressable.
        const blocked = isPrivate && isPrivateModeBlocked(privateModeReason)
        const initializing = isPrivate && privateModeReason === 'initializing'
        // While the engine boots ("Setting up your private wallet…"), lock the
        // WHOLE toggle — including the Public segment. Otherwise a stray click on
        // Public abandons setup mid-flight and the balance/tokens never finish
        // loading. Resting 'creatable' (pre-click) stays clickable so the user
        // can still opt into private mode.
        const setupInProgress = privateModeReason === 'initializing'
        const disabled = blocked || initializing || setupInProgress
        // Web hover-tooltip explaining why Private is unavailable (mirrors the
        // extension Header/Routes). Picked up by the global <GlobalTooltip />
        // mounted in src/mobile-web/AppInit.tsx via the `data-tooltip` attr.
        // 'creatable' is a resting, clickable state on mobile-web — no explainer
        // tooltip (the old "Setting up…" copy was misleading before any click).
        const tooltipContent =
          unavailable && privateModeReason !== 'creatable'
            ? t(PRIVATE_MODE_TOOLTIP[privateModeReason as Exclude<PrivateModeReason, 'available'>])
            : ''
        const segment = (
          <Pressable
            key={id}
            testID={isPrivate ? 'mode-toggle-private' : 'mode-toggle-public'}
            disabled={disabled && !allowRetry}
            onPress={() => {
              if (allowRetry) return onRetryRailgunInit()
              if (!disabled) onModeChange(id)
            }}
            style={{
              position: 'relative',
              overflow: 'hidden',
              flex: stretch ? 1 : undefined,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: segGap,
              paddingVertical: compact ? 3 : 6,
              paddingHorizontal: compact ? 8 : 16,
              borderRadius: RADII.pill,
              opacity: disabled ? 0.5 : 1,
              borderWidth: isOn ? 1 : 0,
              borderColor: 'rgba(255,255,255,0.22)'
            }}
          >
            {/* active pill: CSS gradient fill (blue=public, orange=private) */}
            {isOn && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: RADII.pill,
                  backgroundImage: MODE_TOGGLE_GRADIENT_CSS[id],
                  pointerEvents: 'none'
                }}
              />
            )}
            {/* content above the gradient (the positioned div would otherwise paint over it) */}
            <View style={{ zIndex: 1, flexDirection: 'row', alignItems: 'center', gap: segGap }}>
              <Icon width={iconSize} height={iconSize} color={isOn ? '#fff' : inactiveText} />
              <Text fontSize={fontSize} weight="semiBold" color={isOn ? '#fff' : inactiveText}>
                {t(label)}
              </Text>
            </View>
          </Pressable>
        )

        // Wrap the disabled Private segment so the global tooltip can anchor to
        // it (a disabled Pressable still receives pointermove on the wrapper).
        if (isPrivate && tooltipContent) {
          return (
            <View
              key={id}
              style={{ flex: stretch ? 1 : undefined }}
              // @ts-ignore - web-only data attribute consumed by <GlobalTooltip />
              dataSet={createGlobalTooltipDataSet({
                id: `mode-toggle-private-${privateModeReason}`,
                content: tooltipContent
              })}
            >
              {segment}
            </View>
          )
        }

        return segment
      })}
    </View>
  )
}

export default React.memo(ModeToggle)
